import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Stethoscope, BookOpen, UserCheck, ArrowRight, Heart, Play, Image as ImageIcon, FileText, Building2, GraduationCap } from 'lucide-react';
import { LIBRARY_RESOURCES, FEATURED_RESOURCES } from '../data/library';
import { ASSETS, CENTRES } from '../data/content';

const HERO_BG =
  'https://static.prod-images.emergentagent.com/jobs/c65da21f-1264-4a67-a4d1-f77ef71bc512/images/4176cff4834d27cbe2da611b1630694d6ba9f0d6d7d5ef6365a7fd9fc61e0ac5.png';
// Homepage hero images now come from ASSETS in /app/frontend/src/data/content.js.
// (Founder photo + landing hero are managed in a single content manifest.)

export default function Landing() {
  const { lang, t } = useLanguage();

  const features = [
    { icon: Stethoscope, titleKey: 'feature_selfcheck_title', descKey: 'feature_selfcheck_desc' },
    { icon: BookOpen, titleKey: 'feature_teaching_title', descKey: 'feature_teaching_desc' },
    { icon: UserCheck, titleKey: 'feature_booking_title', descKey: 'feature_booking_desc' },
  ];

  return (
    <div data-testid="landing-page" className="animate-fade-in-up">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ivory/50 via-ivory/80 to-ivory" />
        <div className="container-app pt-12 pb-20 sm:pt-20 sm:pb-28">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-light text-sage text-xs font-semibold uppercase tracking-wider mb-5">
                <Heart size={12} /> {lang === 'zh' ? '温柔陪伴 · 专业支援' : 'Gentle care · Professional support'}
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-ink leading-tight tracking-tight mb-6">
                {t('landing_title')}
              </h1>
              <p className="text-lg text-ink-secondary leading-relaxed mb-8 max-w-xl">
                {t('landing_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <Link
                  to="/self-check"
                  data-testid="landing-start-selfcheck"
                  className="btn-primary group"
                >
                  {t('landing_btn_start')}
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/milk-log" data-testid="landing-log-milk" className="btn-secondary">
                  {t('landing_btn_log')}
                </Link>
                <Link to="/booking" data-testid="landing-book" className="btn-outline">
                  {t('landing_btn_book')}
                </Link>
              </div>
              <div className="mt-8 p-4 bg-paper/70 backdrop-blur rounded-2xl border border-border/40 text-xs sm:text-sm text-ink-secondary leading-relaxed">
                {t('landing_disclaimer')}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 rounded-full bg-terracotta-light blur-2xl" aria-hidden="true" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-sage-light blur-3xl" aria-hidden="true" />
              <img
                src={ASSETS.landingHero}
                alt={lang === 'zh' ? '泌乳辅导员与妈妈和宝宝' : 'Lactation peer counsellor with mother and baby'}
                loading="eager"
                className="relative w-full aspect-[4/5] object-cover object-[center_35%] rounded-[2rem] shadow-xl border-4 border-paper bg-subtle"
                data-testid="landing-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container-app py-12 sm:py-20">
        <h2 className="font-heading text-3xl font-semibold text-ink mb-10 text-center">
          {t('landing_features_title')}
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.titleKey}
                data-testid={`feature-card-${i}`}
                className="card-base p-6 sm:p-8"
              >
                <div className="w-12 h-12 rounded-2xl bg-terracotta-light flex items-center justify-center mb-5">
                  <Icon size={20} className="text-terracotta" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-ink mb-2">
                  {t(f.titleKey)}
                </h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{t(f.descKey)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Founder */}
      <section className="container-app py-12 sm:py-20">
        <div className="card-base p-6 sm:p-10 grid md:grid-cols-[220px_1fr] gap-6 md:gap-10 items-center">
          <div className="relative mx-auto md:mx-0">
            <div className="absolute -top-3 -left-3 w-14 h-14 rounded-full bg-terracotta-light/70 blur-2xl" aria-hidden="true" />
            <img
              src={ASSETS.founder}
              alt={lang === 'zh' ? '创办人 Soo Li Theng' : 'Founder Soo Li Theng'}
              loading="lazy"
              className="relative w-40 h-40 md:w-48 md:h-48 rounded-3xl object-cover border-4 border-paper shadow-md"
              data-testid="founder-image"
            />
            <p className="mt-2 text-center text-[10px] uppercase tracking-[0.18em] text-ink-tertiary">
              {lang === 'zh' ? '创办人 / Director' : 'Founder / Director'}
            </p>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-2 block">
              {t('founder_title')}
            </span>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3">
              {t('founder_name')}
            </h3>
            <p className="text-ink-secondary leading-relaxed mb-4">
              {lang === 'zh'
                ? '由 Soo Li Theng 创办，政府认证护士、国家认证讲师、泌乳辅导员，拥有 15 年护理经验，并与雪隆区 10 家以上月子中心合作。'
                : 'Founded by Soo Li Theng, registered nurse, certified trainer, and lactation peer counsellor with 15 years of nursing experience and collaborations with 10+ confinement centres across KL/Selangor.'}
            </p>
            <Link to="/about" className="inline-flex items-center text-sm font-semibold text-terracotta hover:underline" data-testid="landing-about-btn">
              {lang === 'zh' ? '了解我们' : 'About Us'} <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Confinement-centre collaborations preview */}
      <section className="container-app py-12 sm:py-16" data-testid="landing-centres">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-2 block inline-flex items-center gap-2">
              <Building2 size={12} /> {lang === 'zh' ? '合作伙伴' : 'Partners'}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink">
              {lang === 'zh' ? '合作月子中心' : 'Confinement Centre Collaborations'}
            </h2>
            <p className="text-sm text-ink-secondary mt-2 max-w-2xl leading-relaxed">
              {lang === 'zh'
                ? '我们目前与雪隆区 10 家以上月子中心合作，协助妈妈处理产后哺乳、涨奶、堵奶、亲喂、奶量和挤奶器相关问题。'
                : 'We collaborate with more than 10 confinement centres across KL/Selangor, supporting mothers with postpartum breastfeeding, engorgement, clogged ducts, direct latch, milk supply, and breast pump-related concerns.'}
            </p>
          </div>
          <Link to="/about" className="inline-flex items-center text-sm font-semibold text-sage hover:underline" data-testid="landing-centres-link">
            {lang === 'zh' ? '查看全部' : 'See all'} <ArrowRight size={14} className="ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {/* Show centres with real logos first so beta visitors immediately see trust signals. */}
          {[...CENTRES.filter((c) => c.image), ...CENTRES.filter((c) => !c.image)].slice(0, 5).map((c) => (
            <div
              key={c.id}
              data-testid={`landing-centre-${c.id}`}
              className="card-base aspect-[3/2] p-3 flex flex-col items-center justify-center text-center bg-paper"
            >
              {c.image ? (
                <img
                  src={c.image}
                  alt={lang === 'zh' ? c.name_zh : c.name_en}
                  loading="lazy"
                  className="max-h-10 sm:max-h-12 w-auto object-contain mb-1.5"
                />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-subtle flex items-center justify-center mb-1.5">
                  <Building2 size={14} className="text-ink-tertiary" />
                </div>
              )}
              <span className="text-[10px] sm:text-xs font-medium text-ink leading-tight">
                {lang === 'zh' ? c.name_zh : c.name_en}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Teaching / courses preview */}
      <section className="container-app py-12 sm:py-16" data-testid="landing-courses">
        <div className="card-base p-6 sm:p-10 bg-gradient-to-br from-terracotta-light/30 to-paper grid md:grid-cols-[1fr_220px] gap-6 md:gap-10 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-2 block inline-flex items-center gap-2">
              <GraduationCap size={12} /> {lang === 'zh' ? '教育与课程' : 'Education & Courses'}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3">
              {lang === 'zh' ? '线下母婴课程' : 'Offline Mother-Baby Class'}
            </h2>
            <p className="text-sm text-ink-secondary leading-relaxed mb-4">
              {lang === 'zh'
                ? '我们也提供母婴课程、哺乳教育、挤奶器指导和新手父母课程。'
                : 'We also provide mother-baby classes, breastfeeding education, pump guidance, and new parent courses.'}
            </p>
            <Link to="/library" className="inline-flex items-center text-sm font-semibold text-terracotta hover:underline" data-testid="landing-courses-link">
              {lang === 'zh' ? '浏览资料库' : 'Browse the library'} <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>
          <div className="relative mx-auto md:mx-0">
            {ASSETS.offlineClass ? (
              <img
                src={ASSETS.offlineClass}
                alt={lang === 'zh' ? '线下母婴课程' : 'Offline Mother-Baby Class'}
                loading="lazy"
                className="w-48 h-48 md:w-52 md:h-52 rounded-3xl object-cover border-4 border-paper shadow-md"
              />
            ) : (
              <div
                className="w-48 h-48 md:w-52 md:h-52 rounded-3xl bg-gradient-to-br from-sage-light via-paper to-terracotta-light/60 flex flex-col items-center justify-center border-4 border-paper shadow-md text-center px-4"
                data-testid="landing-courses-placeholder"
              >
                <GraduationCap size={26} className="text-sage mb-2" />
                <span className="text-xs font-medium text-ink-secondary">
                  {lang === 'zh' ? '课程实拍 · 即将上传' : 'Class photo coming soon'}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Free Help Before Booking */}
      <section className="container-app py-12 sm:py-16">
        <div className="card-base p-6 sm:p-10 bg-gradient-to-br from-sage-light/40 to-paper">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-2 block">
            {lang === 'zh' ? '免费支持' : 'Free Support'}
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3">
            {t('landing_free_help_title')}
          </h2>
          <p className="text-ink-secondary leading-relaxed max-w-2xl mb-6">
            {t('landing_free_help_body')}
          </p>
          <Link to="/self-check" className="btn-primary" data-testid="landing-free-help-cta">
            {t('landing_btn_start')} <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>

      {/* Library Preview */}
      <section className="container-app py-12 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-2 block">
              {lang === 'zh' ? '资料库' : 'Library'}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink">
              {t('landing_library_preview_title')}
            </h2>
          </div>
          <Link to="/library" className="inline-flex items-center text-sm font-semibold text-terracotta hover:underline" data-testid="landing-library-link">
            {t('landing_library_browse')} <ArrowRight size={14} className="ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURED_RESOURCES.map((id) => {
            const r = LIBRARY_RESOURCES.find((x) => x.id === id);
            if (!r) return null;
            const Icon = r.type === 'video' ? Play : r.type === 'image_guide' ? ImageIcon : FileText;
            return (
              <Link
                key={id}
                to="/library"
                data-testid={`landing-featured-${id}`}
                className="card-interactive overflow-hidden group"
              >
                <div className="h-28 bg-gradient-to-br from-terracotta-light/50 via-paper to-sage-light/40 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-paper/80 backdrop-blur flex items-center justify-center">
                    <Icon size={18} className="text-ink-secondary" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-heading font-semibold text-ink leading-snug group-hover:text-terracotta transition-colors line-clamp-2">
                    {lang === 'zh' ? r.title_zh : r.title_en}
                  </h3>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
